import { Button, EmptyState, Skeleton } from "@design";
import { BookOpen, Copy, FileUp, Inbox, Plus } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Page } from "@/components/Page";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { useSpacePageTrail } from "@/features/spaces/spaceCrumb";
import { useDuplicates } from "./duplicates/useDuplicates";
import { ImportKnowledgeModal } from "./import/ImportKnowledgeModal";
import { useInbox } from "./inbox/useInbox";
import { KnowledgeTabs } from "./KnowledgeTabs";
import { NewPageModal } from "./NewPageModal";
import { useWikiFiles } from "./useWiki";
import { WikiDocument } from "./WikiDocument";
import { WikiFileList } from "./WikiFileList";

/**
 * A space's Knowledge: its wiki of markdown pages, which is the knowledge
 * graph's source of truth (docs/design/knowledge-graph/KNOWLEDGE_GRAPH_DESIGN.md). The space's
 * uploaded files are the area's other tab (`KnowledgeTabs`). The selected page
 * lives in `?file=` so links and the browser history work.
 *
 * Desktop: page list beside the open page. Phones: the list, or the open page
 * with a way back.
 */
export function KnowledgePage() {
  const { t } = useTranslation("knowledge");
  const { spaceUuid = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const selected = params.get("file");
  const files = useWikiFiles(spaceUuid);
  // Non-members see only the space's org pages (Phase 7): no tabs, no editing.
  const isMember = files.data?.isMember ?? true;
  const spaceName = files.data?.spaceName;
  const trail = useSpacePageTrail(spaceUuid, spaceName);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const canEdit = files.data?.canEdit ?? false;
  const { knowledgeExtractionEnabled } = useCapabilities();

  const open = (path: string | null) => setParams(path ? { file: path } : {});
  const inboxButton = <InboxButton />;
  const newPage = canEdit && (
    <Button variant="primary" size="sm" leadingIcon={<Plus size={16} aria-hidden />} onClick={() => setCreating(true)}>
      {t("newPage")}
    </Button>
  );

  const subtitleKey = isMember ? "subtitle" : "subtitleShared";
  return (
    <>
      <PageBreadcrumbs trail={trail} current={t("title")} />
      <Page
        title={t("title")}
        subtitle={spaceName ? t(subtitleKey, { space: spaceName }) : undefined}
        actions={
          <>
            {inboxButton}
            {canEdit && knowledgeExtractionEnabled && (
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<FileUp size={16} aria-hidden />}
                onClick={() => setImporting(true)}
              >
                {t("import.open")}
              </Button>
            )}
            {canEdit && files.data && files.data.files.length > 1 && <DuplicatesButton spaceUuid={spaceUuid} />}
            {files.data && files.data.files.length > 0 ? newPage || null : null}
          </>
        }
      >
        {isMember && <KnowledgeTabs spaceUuid={spaceUuid} active="wiki" />}
        <WikiBody
          files={files}
          selected={selected}
          spaceUuid={spaceUuid}
          canEdit={canEdit}
          newPage={newPage || undefined}
          onOpen={open}
        />
        {importing && <ImportKnowledgeModal spaceUuid={spaceUuid} onClose={() => setImporting(false)} />}
        {creating && (
          <NewPageModal
            spaceUuid={spaceUuid}
            onClose={() => setCreating(false)}
            onCreated={(path) => {
              setCreating(false);
              open(path);
            }}
          />
        )}
      </Page>
    </>
  );
}

/** The page list and the open page; or loading, an error, or an empty wiki. */
function WikiBody({
  files,
  selected,
  spaceUuid,
  canEdit,
  newPage,
  onOpen,
}: Readonly<{
  files: ReturnType<typeof useWikiFiles>;
  selected: string | null;
  spaceUuid: string;
  canEdit: boolean;
  newPage: ReactNode;
  onOpen: (path: string | null) => void;
}>) {
  const { t } = useTranslation("knowledge");
  if (files.isPending) return <Skeleton className="h-64 w-full" />;
  if (files.isError) {
    return (
      <Text role="alert" tone="error">
        {t("loadFailed")}
      </Text>
    );
  }
  if (files.data.files.length === 0 && !selected) {
    return (
      <EmptyState
        icon={<BookOpen />}
        title={t("empty.title")}
        body={canEdit ? t("empty.editorBody") : t("empty.viewerBody")}
        cta={newPage}
      />
    );
  }
  return (
    <div className="grid gap-6 md:grid-cols-[16rem_minmax(0,1fr)]">
      <div className={selected ? "hidden md:block" : undefined}>
        <WikiFileList files={files.data.files} selected={selected} onSelect={onOpen} />
      </div>
      <div className={selected ? "min-w-0" : "hidden min-w-0 md:block"}>
        {selected ? (
          <WikiDocument key={selected} spaceUuid={spaceUuid} path={selected} onClose={() => onOpen(null)} />
        ) : (
          <Text tone="tertiary">{t("selectPage")}</Text>
        )}
      </div>
    </div>
  );
}

/** Your knowledge inbox, when this deployment learns from chats. */
function InboxButton() {
  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  const { knowledgeExtractionEnabled } = useCapabilities();
  const inbox = useInbox({ enabled: knowledgeExtractionEnabled });
  const count = inbox.data?.pendingCount ?? 0;
  if (!knowledgeExtractionEnabled) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={<Inbox size={16} aria-hidden />}
      onClick={() => navigate("/knowledge/inbox")}
    >
      {count > 0 ? t("inbox.openWithCount", { count }) : t("inbox.open")}
    </Button>
  );
}

/** For editors: the space's possible duplicate pages, with how many are waiting. */
function DuplicatesButton({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  const count = useDuplicates(spaceUuid).data?.length ?? 0;
  return (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={<Copy size={16} aria-hidden />}
      onClick={() => navigate(`/spaces/${spaceUuid}/knowledge/duplicates`)}
    >
      {count > 0 ? t("duplicates.openWithCount", { count }) : t("duplicates.open")}
    </Button>
  );
}
